import {
  Button,
  Divider,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { ConfirmModal } from 'components/ConfirmModal';
import { useEventContext } from 'contexts/EventContext';
import { useNavigate } from 'react-router-dom';
import {
  useGetCentersQuery,
  useGetGameStatsQuery,
  useGetTeamByIdQuery,
  useVerifyGameMutation,
} from '../../app/api';
import { GameView } from '../../types';
import { TeamStats } from './TeamStats';

interface GameViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  game: GameView;
}

const GameViewModal: React.FC<GameViewModalProps> = ({
  isOpen,
  onClose,
  game,
}) => {
  type TeamCenterData = {
    centerId?: number;
    centerName?: string;
    center?: string;
  };

  const resolveTeamCenterName = (
    team: TeamCenterData | undefined,
    centers: Array<{ id: number; name: string }> | undefined
  ) => {
    if (!team) return undefined;
    if (team.centerName) return team.centerName;
    if (team.center) return team.center;
    if (team.centerId == null) return undefined;
    return centers?.find((center) => center.id === team.centerId)?.name;
  };

  const editButtonColor = useColorModeValue('orange', 'blue');
  const navigate = useNavigate();
  const { data } = useGetGameStatsQuery(game.id);
  const { data: centers } = useGetCentersQuery();
  const { data: homeTeamData } = useGetTeamByIdQuery(game.homeTeamId);
  const { data: awayTeamData } = useGetTeamByIdQuery(game.awayTeamId);
  const [verifyGame, { isLoading: verifyLoading }] = useVerifyGameMutation();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const { canPerformEventAction } = useEventContext();

  const isAdmin = canPerformEventAction('EVENT_ADMIN');
  const canVerify = isAdmin && game.played && !game.verified;
  const homeTeamCenter = resolveTeamCenterName(
    homeTeamData as unknown as TeamCenterData,
    centers
  );
  const awayTeamCenter = resolveTeamCenterName(
    awayTeamData as unknown as TeamCenterData,
    centers
  );

  return (
    <Modal size="4xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
          <ModalHeader>
            <HStack>
              <Stack spacing={0} align="start">
                <Heading>
                  {game.homeTeamName} vs. {game.awayTeamName}
                </Heading>
                {(homeTeamCenter || awayTeamCenter) && (
                  <Text fontSize="sm" color="gray.500">
                    {game.homeTeamName}: {homeTeamCenter || '-'} |{' '}
                    {game.awayTeamName}: {awayTeamCenter || '-'}
                  </Text>
                )}
              </Stack>
              <Button
                colorScheme={editButtonColor}
                onClick={() => navigate(`../scoringV2/${game.id}`)}
              >
                {game.played ? 'Start' : 'Edit'} Scoring
              </Button>
              {canVerify && (
                <Button colorScheme={editButtonColor} onClick={onConfirmOpen}>
                  Verify
                </Button>
              )}
            </HStack>
            <ConfirmModal
              isLoading={verifyLoading}
              title="Verify Game"
              message="Are you sure you want to verify this game? It cannot be undone."
              confirmText="Verify"
              cancelText="Cancel"
              isOpen={isConfirmOpen}
              onClose={onConfirmClose}
              onConfirm={async () => {
                await verifyGame({ id: game.id });
                onConfirmClose();
                onClose();
              }}
            />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack
              align="stretch"
              w="full"
              direction={{ base: 'column', md: 'row' }}
            >
              <TeamStats
                id={game.homeTeamId}
                name={game.homeTeamName}
                drawId={game.homeTeamDrawId}
                score={game.homeTeamScore}
                won={game.homeTeamScore > game.awayTeamScore}
                players={data?.homeTeam}
                isHome={true}
              />
              <Divider orientation="vertical" />
              <TeamStats
                id={game.awayTeamId}
                name={game.awayTeamName}
                drawId={game.awayTeamDrawId}
                score={game.awayTeamScore}
                won={game.awayTeamScore > game.homeTeamScore}
                players={data?.awayTeam}
                isHome={false}
              />
            </Stack>
          </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default GameViewModal;
